import { useEffect, useState, useCallback, useRef } from "react";
import { devLog } from "../../../utils/devLog";
import { useTranslation } from "../../../hooks/useTranslation";
import { CALLBACKS, triggerCallback } from "../../../utils/commonUtils";
import { useCheckout } from "../../../context/CheckoutContext";
import { processPayment, fetchGatewayConnection } from "../../../api/api";
import styled from "styled-components";
import { useStyles } from "../../../context/StyleContext";
import { fetchSession, updateSession } from "../../../utils/paymentSession";

// Styled components for loading state
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid ${props => props.$styles?.colors?.primary || '#2563eb'};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: ${props => props.$styles?.colors?.textSecondary || '#666'};
  font-size: ${props => props.$styles?.typography?.fontSizes?.md || '14px'};
  font-family: ${props => props.$styles?.typography?.fontFamily || 'Arial, sans-serif'};
  margin: 0;
`;

const ReadyMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
`;

const KakaopayLogo = styled.div`
  width: 80px;
  height: 80px;
  background: #FFEB00;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  font-weight: bold;
  font-size: 14px;
  color: #3C1E1E;
`;

const MessageText = styled.p`
  color: ${props => props.$styles?.colors?.textPrimary || '#333'};
  font-size: ${props => props.$styles?.typography?.fontSizes?.md || '14px'};
  font-family: ${props => props.$styles?.typography?.fontFamily || 'Arial, sans-serif'};
  margin: 0;
  line-height: 1.5;
`;

const AltruonCheckoutComKakaopayForm = ({
  formData,
  itemDetails,
  billingPlatformId,
  routingEntries,
  planId,
  platformItemDetails,
  checkoutPageConfDto,
  checkoutProcessDto,
  transparent,
  prefetchedGatewayConnection,
  tenantId,
}) => {
  const queryParams = new URLSearchParams(window.location.search);
  const sessionId = queryParams.get("session_id");

  const { t } = useTranslation();
  const { triggerPaymentMethodEvent, resetTrigger } = useCheckout();
  const { styles } = useStyles();
  
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Use refs to avoid stale closure issues
  const checkoutProcessDtoRef = useRef(checkoutProcessDto);
  const sessionIdRef = useRef(sessionId);
  const isProcessingRef = useRef(false);

  // Keep refs updated
  useEffect(() => {
    checkoutProcessDtoRef.current = checkoutProcessDto;
    sessionIdRef.current = sessionId;
  }, [checkoutProcessDto, sessionId]);

  /**
   * Get email from multiple possible sources
   */
  const getEmail = useCallback((sessionData, latestSession, eventData) => {
    const email = formData?.email 
      || eventData?.payload?.customerData?.email
      || eventData?.payload?.email
      || eventData?.sessionData?.customerData?.email
      || eventData?.customerData?.email
      || sessionData?.customerData?.email 
      || sessionData?.email
      || latestSession?.customerData?.email
      || latestSession?.email
      || null;
    
    devLog('[Kakaopay] Email lookup sources:', {
      'formData?.email': formData?.email,
      'eventData?.payload?.customerData?.email': eventData?.payload?.customerData?.email,
      'eventData?.payload?.email': eventData?.payload?.email,
      'eventData?.sessionData?.customerData?.email': eventData?.sessionData?.customerData?.email,
      'eventData?.customerData?.email': eventData?.customerData?.email,
      'sessionData?.customerData?.email': sessionData?.customerData?.email,
      'sessionData?.email': sessionData?.email,
      'latestSession?.customerData?.email': latestSession?.customerData?.email,
      'latestSession?.email': latestSession?.email,
    });
    
    return email;
  }, [formData]);

  /**
   * Validate required fields for Kakaopay
   */
  const validateKakaopayData = useCallback((email, sessionData) => {
    const missingFields = [];

    // Email is mandatory for Kakaopay
    if (!email) {
      missingFields.push('email');
    }

    // Amount and currency are required
    if (!sessionData?.paymentData?.amount) {
      missingFields.push('amount');
    }
    if (!sessionData?.paymentData?.currency) {
      missingFields.push('currency');
    }
    if (!sessionData?.paymentData?.gatewayConnectionId) {
      missingFields.push('gatewayConnectionId');
    }

    return missingFields;
  }, []);

  /**
   * Handle Kakaopay payment processing
   * @param {Object} eventData - Optional data from makePayment event
   */
  const handleKakaopayPayment = useCallback(async (eventData = null) => {
    const currentSessionData = checkoutProcessDtoRef.current;
    const currentSessionId = sessionIdRef.current;

    devLog('[Kakaopay] Starting payment process...');
    devLog('[Kakaopay] Session data from props:', currentSessionData);
    devLog('[Kakaopay] Event data received:', eventData);

    try {
      // Small delay to allow session update to propagate
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Fetch latest session to get the most up-to-date data
      const latestSession = await fetchSession(currentSessionId);
      devLog('[Kakaopay] Latest session from API:', latestSession);

      // Get email from multiple possible sources (including event data)
      const email = getEmail(currentSessionData, latestSession, eventData);
      devLog('[Kakaopay] Resolved email:', email);

      // Use the most complete session data available
      const sessionData = latestSession || currentSessionData;

      // Validate required fields
      const missingFields = validateKakaopayData(email, sessionData);
      
      if (missingFields.length > 0) {
        devLog('[Kakaopay] Missing required fields:', missingFields);
        
        // Send onDataRequired callback
        triggerCallback(CALLBACKS.DATA_REQUIRED, {
          missingFields,
          message: missingFields.includes('email') 
            ? t('checkout.paymentMethods.kakaopay.emailRequired') || 'Customer email is required for Kakaopay payments'
            : t('checkout.paymentMethods.kakaopay.missingData') || 'Required payment data is missing',
        });
        return;
      }

      setIsProcessing(true);

      // Build customer data with email from resolved source
      const customerData = {
        ...sessionData.customerData,
        email: email,
        firstName: formData?.firstName || sessionData?.customerData?.firstName,
        lastName: formData?.lastName || sessionData?.customerData?.lastName,
        phone: formData?.phone || sessionData?.customerData?.phone,
      };

      // Prepare payment request payload (matching other payment methods' format)
      const paymentPayload = {
        paymentData: {
          gatewayConnectionId: sessionData.paymentData.gatewayConnectionId,
          gatewayName: sessionData.paymentData.gatewayName || 'checkoutcom',
          paymentMethod: 'kakaopay',
          // amount: sessionData.paymentData.amount,
          // currency: sessionData.paymentData.currency,
          transactionId: '',
          additionalData: {
            payment_method_flow: 'REDIRECT',
            ...(sessionData.paymentData.additionalData || {}),
          },
        },
        customerData: customerData,
        billingData: sessionData.billingData,
      };

      devLog('[Kakaopay] Sending payment request:', paymentPayload);
      devLog('[Kakaopay] Using billingPlatformId:', billingPlatformId);
      devLog('[Kakaopay] Using sessionId:', currentSessionId);

      // Update session with payment data before processing
      await updateSession(currentSessionId, paymentPayload);
      
      // Small delay after session update
      await new Promise(resolve => setTimeout(resolve, 300));

      // Call backend API using existing processPayment function
      const response = await processPayment(billingPlatformId, currentSessionId, paymentPayload);

      devLog('[Kakaopay] Backend response:', response);
      devLog('[Kakaopay] Backend response data:', response.data);

      // Handle successful response (status 200)
      if (response.status === 200) {
        const data = response.data;
        
        // Check if redirect is required
        const redirectUrl = data.redirect_url || data.redirectUrl;
        
        if (redirectUrl) {
          devLog('[Kakaopay] Redirect required:', redirectUrl);
          
          triggerCallback(CALLBACKS.ACTION_REQUIRED, {
            actionType: 'redirect',
            redirectUrl: redirectUrl,
            paymentMethod: 'kakaopay',
            transactionId: data.transactionId,
          });

          // Also notify parent window
          window.parent.postMessage({
            type: 'onActionRequired',
            payload: {
              actionType: 'redirect',
              redirectUrl: redirectUrl,
              paymentMethod: 'kakaopay',
              transactionId: data.transactionId,
            },
          }, '*');
          
          return;
        }
        
        // Direct success (no redirect needed)
        devLog('[Kakaopay] Payment successful:', data.transactionId);
        
        triggerCallback(CALLBACKS.SUCCESS, {
          status: 'success',
          transactionId: data.transactionId,
          paymentMethod: 'kakaopay',
          message: t('checkout.paymentMethods.kakaopay.success') || 'Payment successfully initiated.',
        });

        window.parent.postMessage({
          type: 'paymentSuccess',
          data: { 
            transactionId: data.transactionId,
            paymentMethod: 'kakaopay',
          },
        }, '*');
        
        return;
      }
      
      // Non-200 response
      throw new Error(response.data?.message || 'Payment failed');

    } catch (error) {
      console.error('[Kakaopay] Error:', error);
      
      const errorMessage = error.response?.data?.error?.message 
        || error.response?.data?.message 
        || error.message 
        || t('checkout.paymentMethods.kakaopay.error') 
        || 'Failed to process Kakaopay payment';
      
      const errorCode = error.response?.data?.error?.code || 'PAYMENT_ERROR';

      triggerCallback(CALLBACKS.FAILURE, {
        status: 'failed',
        error: {
          code: errorCode,
          message: errorMessage,
          paymentMethod: 'kakaopay',
        },
      });

      window.parent.postMessage({
        type: 'onFailure',
        payload: {
          error: {
            code: errorCode,
            message: errorMessage,
            paymentMethod: 'kakaopay',
          },
        },
      }, '*');
      
    } finally {
      setIsProcessing(false);
    }
  }, [t, validateKakaopayData, getEmail, formData, billingPlatformId]);

  /**
   * Handle triggerPaymentMethodEvent from checkout context
   */
  useEffect(() => {
    if (triggerPaymentMethodEvent && !isProcessingRef.current) {
      isProcessingRef.current = true;
      handleKakaopayPayment(null).finally(() => {
        isProcessingRef.current = false;
        resetTrigger();
      });
    }
  }, [triggerPaymentMethodEvent, handleKakaopayPayment, resetTrigger]);

  /**
   * Listen for "makePayment" from parent → trigger payment
   */
  useEffect(() => {
    const handleMessage = (event) => {
      if (event?.data?.type === "makePayment" && !isProcessingRef.current) {
        devLog('[Kakaopay] Received makePayment event');
        devLog('[Kakaopay] makePayment event full data:', event?.data);
        devLog('[Kakaopay] makePayment event payload:', event?.data?.payload);
        devLog('[Kakaopay] makePayment event sessionData:', event?.data?.sessionData);
        isProcessingRef.current = true;
        // Pass event data to payment handler in case it contains customer data
        handleKakaopayPayment(event?.data).finally(() => {
          isProcessingRef.current = false;
        });
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleKakaopayPayment]);

  /**
   * Notify parent that form is ready
   */
  useEffect(() => {
    // Mark as ready once we have session data
    if (checkoutProcessDto?.paymentData) {
      // Notify parent that Kakaopay form is ready
      window.parent.postMessage({
        type: 'formReady',
        data: {
          paymentMethod: 'kakaopay',
          gateway: 'checkoutcom',
        },
      }, '*');
    }
  }, [checkoutProcessDto]);

  // Render loading state
  if (isProcessing) {
    return (
      <LoadingContainer>
        <LoadingSpinner $styles={styles} />
        <LoadingText $styles={styles}>
          {t('checkout.paymentMethods.kakaopay.processing') || 'Processing Kakaopay payment...'}
        </LoadingText>
      </LoadingContainer>
    );
  }

  // Render ready state
  return (
    <ReadyMessage>
      <KakaopayLogo>
        KakaoPay
      </KakaopayLogo>
      <MessageText $styles={styles}>
        {t('checkout.paymentMethods.kakaopay.readyMessage') || 'You will be redirected to Kakaopay to complete your payment.'}
      </MessageText>
    </ReadyMessage>
  );
};

export default AltruonCheckoutComKakaopayForm;
